<template>
  <!-- ======================= -->
  <!-- TAB: WEAPON             -->
  <!-- ======================= -->
  <div>
    <!-- ----------------- -->
    <!--   WEAPON DETAILS  -->
    <!-- ----------------- -->
    <div class="row mt-2 mb-2"></div>
    <p class="bb-hdg mt-2 mb-3">Weapon Details</p>
    <!-- -------------- -->
    <!--      ROW 1     -->
    <!-- -------------- -->
    <div class="row mt-2 mb-2">
      <!-- ----------------------- -->
      <!--   worn weapon (switch)  -->
      <!-- ----------------------- -->
      <div
        v-if="isWearableWeapon && showWornSwitch"
        class="col-auto pe-3"
      >
        <enhanced-toggle
          style="
            --toggleWidth: 260px;
            --labelWidth: 160px;
            --switchWidth: 90px;
            --switchPos: 160px;
            height: 50px;
            border-radius: 10px;
            background-color: white;
          "
          :styleOff="`default`"
          :styleOn="`primary`"
          :labelOn="`Worn`"
          :labelOff="`Not Worn`"
          :checked="activeItem.worn"
          @update:modelValue="onWornToggle"
        />
      </div>
      <!-- --------------------- -->
      <!--   weapon state (r/o)  -->
      <!-- --------------------- -->
      <div
        v-if="isWeaponOrArmour && showStateField"
        class="col-5 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            :class="[
              'form-control',
              'text-center',
              activeItem.hp === 0 ? 'bb-item-warning' : '',
            ]"
            :value="computeItemState"
            id="state"
            disabled
          />
          <label
            :class="[
              'bb-float-label',
              'optional',
              activeItem.hp === 0 ? 'bb-item-warning-label' : '',
            ]"
            for="state"
          >
            State
          </label>
        </div>
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 2   -->
    <!-- ---------- -->
    <div class="row mt-2 mb-2">
      <!-- -------- -->
      <!--    AP    -->
      <!-- -------- -->
      <div class="col-4 form-group">
        <CounterEmit
          ref="apCounter"
          :pass_ref="'ap'"
          :pass_label="'AP'"
          :pass_count="localItem.ap ?? activeItem.ap ?? 0"
          :pass_min="0"
          :pass_max="maxAp"
          :pass_digits="2"
          @change="onCounterChanged"
        />
      </div>
      <!-- -------- -->
      <!--    HP    -->
      <!-- -------- -->
      <div class="col-4 form-group">
        <CounterEmit
          ref="hpCounter"
          :pass_ref="'hp'"
          :pass_label="'HP'"
          :pass_count="localItem.hp ?? activeItem.hp ?? 0"
          :pass_min="0"
          :pass_max="maxHp"
          :pass_digits="2"
          @change="onCounterChanged"
        />
      </div>
      <div
        v-if="showPwrStorageCounter"
        class="col-4 form-group"
      >
        <CounterEmit
          ref="pwrStorageCounter"
          :pass_ref="'pwr_storage'"
          :pass_label="'Pwr Str'"
          :pass_count="localItem.pwr_storage ?? activeItem.pwr_storage ?? 0"
          :pass_min="0"
          :pass_max="20"
          :pass_digits="2"
          @change="onCounterChanged"
        />
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 3   -->
    <!-- ---------- -->
    <div class="row mt-2 mb-2">
      <template v-if="effectiveItemType == 'Melee Weapon'">
        <!-- ----------- -->
        <!--    impale   -->
        <!-- ----------- -->
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="activeItem.impale"
              id="impale"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="impale"
              >Impale</label
            >
          </div>
        </div>
        <!-- -------------- -->
        <!--    set charge   -->
        <!-- --------------- -->
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="activeItem.set_charge"
              id="set_charge"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="set_charge"
              >Set Charge</label
            >
          </div>
        </div>
      </template>
      <template v-else-if="effectiveItemType == 'Ranged Weapon'">
        <!-- --------- -->
        <!--    load   -->
        <!-- --------- -->
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="activeItem.load"
              id="load"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="load"
              >Load (CA)</label
            >
          </div>
        </div>
        <!-- ---------- -->
        <!--    range   -->
        <!-- ---------- -->
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="activeItem.range"
              id="range"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="range"
              >Range (m)</label
            >
          </div>
        </div>
      </template>
      <!-- ------------- -->
      <!--  damage dice  -->
      <!-- ------------- -->
      <div class="col-6 form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="weaponDamageDiceSummary"
            id="damage_dice"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="damage_dice"
            >Damage Dice</label
          >
        </div>
      </div>
    </div>
    <!-- ---------- -->
    <!--   ROW 4A   -->
    <!-- ---------- -->
    <!-- --------------------------- -->
    <!--   worn locations (input)    -->
    <!-- --------------------------- -->
    <div
      v-if="isWearableWeapon && showWornLocationField"
      class="row mt-2"
    >
      <div class="row mt-2 mb-2"></div>
      <div class="col form-group">
        <div class="form-floating">
          <v-select
            :options="wornLocations"
            label="name"
            :reduce="reduceObject"
            class="form-control"
            placeholder="select..."
            :model-value="localItem.worn_location"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('worn_location') }"
            @update:modelValue="onWornLocationSelect"
            id="worn_location"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label"
            for="worn_location"
            >Worn Location</label
          >
        </div>
      </div>
    </div>
    <!-- ------------ -->
    <!--    skills    -->
    <!-- ------------ -->
    <div class="row mt-2 mb-2"></div>
    <p class="bb-sub-hdg mb-2 mt-2">Weapon Skills</p>
    <!-- ---------- -->
    <!--    ROW 5   -->
    <!-- ---------- -->
    <div class="container-fluid mt-2 mb-2">
      <div
        v-if="showCharacterStats"
        class="row mt-2 mb-2"
      >
        <!-- ----------- -->
        <!--   str cur   -->
        <!-- ----------- -->
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="character?.str_cur"
              id="str_cur"
              disabled
            />
            <label
              class="bb-float-label"
              for="str_cur"
              >Cur Str</label
            >
          </div>
        </div>
        <!-- ------------ -->
        <!--    dex cur   -->
        <!-- ------------ -->
        <div class="col-3 form-group">
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="character?.dex_cur"
              id="dex_cur"
              disabled
            />
            <label
              class="bb-float-label"
              for="dex_cur"
              >Cur Dex</label
            >
          </div>
        </div>
      </div>
      <!-- -------------- -->
      <!-- table headings -->
      <!-- -------------- -->
      <div class="row">
        <div
          :class="showCharacterStats ? 'col-3' : 'col-4'"
          class="text-left"
        >
          <p class="bb-sub-hdg mt-2 mb-1">Name</p>
        </div>
        <div
          v-if="showCharacterStats"
          class="col-2 text-center"
        >
          <p class="bb-sub-hdg mt-2 mb-1">Skilled</p>
        </div>
        <div
          :class="showCharacterStats ? 'col-3' : 'col-4'"
          class="text-center"
        >
          <p class="bb-sub-hdg mt-2 mb-1">Min Str/Dex</p>
        </div>
        <div
          v-if="showCharacterStats"
          class="col-2 text-center"
        >
          <p class="bb-sub-hdg mt-2 mb-1">Mins Met</p>
        </div>
        <div
          :class="showCharacterStats ? 'col-2' : 'col-4'"
          class="text-center"
        >
          <p class="bb-sub-hdg mt-2 mb-1">Damage</p>
        </div>
      </div>
      <!-- ---------- -->
      <!-- skill rows -->
      <!-- ---------- -->
      <div
        class="row mt-2 mb-2 align-items-center bb-tooltip-wrapper"
        v-for="s in weaponSkillRows"
        :key="s.name"
      >
        <!-- ----------- -->
        <!-- skill name  -->
        <!-- ----------- -->
        <div
          :class="showCharacterStats ? 'col-3' : 'col-4'"
          class="form-group"
        >
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="s.name"
              :id="`skill-${s.name}`"
              disabled
            />
            <label
              class="bb-float-label"
              :for="`skill-${s.name}`"
              >Name</label
            >
          </div>
        </div>
        <!-- skilled   -->
        <div
          v-if="showCharacterStats"
          class="col-2 form-group"
        >
          <div class="form-floating">
            <input
              readonly
              :class="[
                'form-control',
                'text-center',
                s.skill === 'No' ? 'bb-item-warning' : '',
              ]"
              :value="s.skill"
              :id="`skilled-${s.name}`"
              disabled
            />
            <label
              :class="[
                'bb-float-label',
                s.skill === 'No' ? 'bb-item-warning-label' : '',
              ]"
              :for="`skilled-${s.name}`"
            >
              Skilled
            </label>
          </div>
        </div>
        <!-- min str/dex -->
        <div
          :class="showCharacterStats ? 'col-3' : 'col-4'"
          class="form-group"
        >
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="`${s.min_str} / ${s.min_dex}`"
              :id="`skill-mins-${s.skill_id ?? s.name}`"
              disabled
            />
            <label
              class="bb-float-label"
              :for="`skill-mins-${s.skill_id ?? s.name}`"
              >Min Str/Dex</label
            >
          </div>
        </div>
        <!-- mins met -->
        <div
          v-if="showCharacterStats"
          class="col-2 form-group"
        >
          <div class="form-floating">
            <input
              readonly
              :class="[
                'form-control',
                'text-center',
                s.met_req === 'No' ? 'bb-item-warning' : '',
              ]"
              :value="s.met_req"
              :id="`skill-met-${s.skill_id ?? s.name}`"
              disabled
            />
            <label
              :class="[
                'bb-float-label',
                s.met_req === 'No' ? 'bb-item-warning-label' : '',
              ]"
              :for="`skill-met-${s.skill_id ?? s.name}`"
            >
              Met
            </label>
          </div>
        </div>
        <!-- damage dice -->
        <div
          :class="showCharacterStats ? 'col-2' : 'col-4'"
          class="form-group"
        >
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="s.damage_dice"
              :id="`skill-damage-dice-${s.skill_id ?? s.name}`"
              disabled
            />
            <label
              class="bb-float-label optional"
              :for="`skill-damage-dice-${s.skill_id ?? s.name}`"
              >Damage</label
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import CounterEmit from './CounterEmit.vue'

export default {
  name: 'ItemWeaponTab',
  components: {
    CounterEmit,
  },
  computed: {
    isWearableWeapon() {
      return !!(this.localItem?.wearable ?? this.activeItem?.wearable)
    },
    showPwrStorageCounter() {
      return !!(
        this.showPwrStorage &&
        this.showCharacterStats &&
        this.character
      )
    },
    weaponDamageDiceSummary() {
      const sourceRows = Array.isArray(this.weaponSkillRows)
        ? this.weaponSkillRows.filter(
            (row) =>
              row &&
              typeof row.damage_dice === 'string' &&
              row.damage_dice.trim() !== '',
          )
        : []

      if (!sourceRows.length) {
        return this.localItem?.damage_dice ?? this.activeItem?.damage_dice ?? ''
      }

      const uniqueDamageDice = [
        ...new Set(sourceRows.map((row) => row.damage_dice.trim())),
      ]

      if (sourceRows.length === 1 || uniqueDamageDice.length === 1) {
        return uniqueDamageDice[0]
      }

      return sourceRows
        .map(
          (row) =>
            `${this.getWeaponSkillLabel(row.name)} (${row.damage_dice.trim()})`,
        )
        .join(' / ')
    },
  },
  methods: {
    getWeaponSkillLabel(skillName) {
      if (!skillName || typeof skillName !== 'string') {
        return 'Skill'
      }

      if (/\b1h\b/i.test(skillName)) return '1H'
      if (/\b2h\b/i.test(skillName)) return '2H'

      return skillName
    },
  },
  props: {
    activeItem: {
      type: Object,
      required: true,
    },
    localItem: {
      type: Object,
      required: true,
    },
    effectiveItemType: {
      type: String,
      required: true,
    },
    wornLocations: {
      type: Array,
      default: () => [],
    },
    errorFields: {
      type: Array,
      required: true,
    },
    maxAp: {
      type: Number,
      required: true,
    },
    maxHp: {
      type: Number,
      required: true,
    },
    isWeaponOrArmour: {
      type: Boolean,
      required: true,
    },
    computeItemState: {
      type: String,
      default: '',
    },
    showStateField: {
      type: Boolean,
      default: true,
    },
    showWornSwitch: {
      type: Boolean,
      default: true,
    },
    showWornLocationField: {
      type: Boolean,
      default: false,
    },
    showCharacterStats: {
      type: Boolean,
      default: true,
    },
    character: {
      type: Object,
      default: null,
    },
    weaponSkillRows: {
      type: Array,
      required: true,
    },
    reduceObject: {
      type: Function,
      required: true,
    },
    onKeyDown: {
      type: Function,
      required: true,
    },
    onWornLocationSelect: {
      type: Function,
      required: true,
    },
    onCounterChanged: {
      type: Function,
      required: true,
    },
    onWornToggle: {
      type: Function,
      required: true,
    },
    showPwrStorage: {
      type: Boolean,
      default: true,
    },
  },
}
</script>
